/**
 * Voxium Script — Spanish locale strings (es, neutral international
 * Spanish, "tú" register).
 *
 * DRAFT — pending native review. Verbatim per Alain's brief; do not
 * rewrite without his sign-off. Single source of truth for every Spanish
 * string rendered on /es — do not hardcode Spanish copy inside
 * components, import from here.
 *
 * Brand/product names kept in English on purpose: Voxium, Voxium
 * Script, Instagram, Facebook, work.voxiumscript.com, and the plan/pack
 * names Standard, Pro, Pro Plus, First Taste, Sip, Deep Dive. Mentor is
 * identical in Spanish (no change). Everything else — Foundation,
 * Council, Studio Head, Story Development, Pitch Meeting Simulation,
 * Q&A — is translated per Alain's glossary (2026-10-17 revision):
 * Foundation -> Fundamentos / Modo Fundamentos, Council -> Consejo,
 * Studio Head -> Jefe de Estudio, Story Development -> Desarrollo de
 * historia, Pitch Meeting Simulation -> Simulación de reunión de pitch,
 * Q&A -> preguntas y respuestas / "Preguntas de seguimiento". "logline"
 * and "pitch" (as a running-text noun) remain lowercase English
 * loanwords — standard practice in Spanish screenwriting.
 */

// "US$" prefix + English-style comma thousands grouping, matching the
// English page's own format exactly (toLocaleString("en-US")) — only
// the "US$" prefix is Spanish-page-specific. Numbers come from the same
// entitlements data the English page reads, never retyped here.
export const priceES = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `US$${n.toLocaleString("en-US")}`;
};

// "o US$1,290/año, ahorras US$258"
export const annualNoteES = (monthly, annual) => {
  const m = Number(monthly);
  const a = Number(annual);
  if (!Number.isFinite(m) || !Number.isFinite(a)) return null;
  const save = m * 12 - a;
  if (save <= 0) return null;
  return `o ${priceES(a)}/año, ahorras ${priceES(save)}`;
};

export const es = {
  locale: "es",

  head: {
    title: "Voxium Script — Análisis de guion para guionistas serios",
    description:
      "Análisis de guion riguroso y honesto, y Desarrollo de historia. Modo Fundamentos, lectura del Consejo, Jefe de Estudio. Diseñado para guionistas que buscan esa reunión.",
  },

  nav: {
    signIn: "Iniciar sesión",
  },

  openingStrip: {
    slug1: "INT. SALA DE GUIONISTAS — NOCHE",
    action1: [
      "Una sola lámpara arde sobre un escritorio lleno de fichas.",
      "Páginas apiladas. Un bolígrafo, destapado. Alguien relee,",
      "otra vez, el inicio de un guion en el que lleva meses trabajando.",
    ],
    cue1: "EL LECTOR",
    paren1: "(sin adular)",
    dialogue1: [
      "La premisa funciona. El protagonista, todavía no.",
      "Nos pides seguir a alguien cuyo deseo nunca",
      "llega a verse claro en la página. Define ese",
      "deseo, y el resto de la estructura podrá responder.",
    ],
    slug2: "INT. OFICINA DEL PRODUCTOR — DÍA",
    action2: [
      "El guion está sobre el escritorio. Los márgenes están llenos de notas.",
      "Una segunda copia descansa al lado, intacta, con la portada",
      "un poco arrugada de tanto leerla.",
    ],
    cue2: "EL PRODUCTOR",
    dialogue2: [
      "Tres actos que funcionan. Un segundo acto que se alarga",
      "ocho páginas. Un clímax en el que creí antes de que",
      "el planteamiento se lo ganara. Recomendaría una reescritura, no un simple visto bueno.",
    ],
    slug3: "EXT. EL PROYECTO CON LUZ VERDE — CONTINUO",
    action3: [
      "La página pasa, por fin, por su propio peso.",
      "El trabajo está listo. El trabajo está listo. El trabajo está listo.",
    ],
    cue3: "EL CONSEJO",
    paren3: "(al unísono)",
    dialogue3: ["Recomendado."],
    pageNumber: "17.",
  },

  hero: {
    headline: "Prepara tu guion para la luz verde.",
    body:
      "Todo guionista desea ver su obra producida. Voxium Script te ofrece un análisis riguroso y honesto, además de desarrollo de historia, a un precio asequible para los guionistas serios. Así, cuando consigas esa reunión, tu guion estará listo.",
    ctaPrimary: "Haz tu primera lectura",
    ctaSecondary: "Mira cómo funciona",
    attributionPrefix: "De los creadores de ",
    attributionBrand: "Voxium",
  },

  intro: {
    headline: "Dondequiera que estés en el proceso, ahí te encontramos.",
    body:
      "El camino de la idea al guion producido no es lineal. Algunos días pones a prueba una premisa. Otros, pules diálogos para una reunión de análisis. Voxium Script está diseñado para encontrarte en cada etapa, con el tipo de lector adecuado en el momento justo.",
  },

  cards: [
    {
      mark: "I",
      eyebrow: "Desarrollo de historia",
      headline: "Pon a prueba la idea antes de escribirla.",
      body:
        "Siéntate con el Mentor, un socio creativo imparcial formado en el oficio de contar historias. Habla sobre la premisa, el protagonista, la pregunta central. Saca a la luz los problemas estructurales pronto, cuando revisar todavía no cuesta nada. Ya sea que estés dando forma a un largometraje, desarrollando un piloto o probando una logline, Desarrollo de historia es donde empieza el trabajo.",
      bestFor:
        "guionistas que prefieren encontrar las grietas estructurales en la idea antes que en el tercer borrador.",
      cta: "Inicia una sesión de desarrollo",
      testId: "card-story-development-es",
    },
    {
      mark: "II",
      eyebrow: "Análisis de guion",
      headline: "La sala lee. La sala decide.",
      body: [
        "Sube tu guion. Obtén Modo Fundamentos, un diagnóstico de oficio que cubre los quince elementos que hacen que una historia funcione. Luego reúne al Consejo: el Lector, el Productor, el Director, la Audiencia. Cada personaje lee desde su propio asiento, con su propia voz. No se ponen de acuerdo entre ellos. No te halagan. Escuchas las preguntas antes de que la sala te las haga.",
        `Si la sala dice que sí, tu guion se gana una lectura más: la del Jefe de Estudio. La perspectiva de quien apuesta. La voz que no solo pregunta "¿está listo esto?", sino "¿es este el tipo de historia en la que invertiría ahora mismo?"`,
      ],
      bestFor:
        "guionistas listos para saber si el borrador funciona, y qué corregir si no es así.",
      cta: "Haz un análisis",
      testId: "card-script-coverage-es",
    },
    {
      mark: "III",
      eyebrow: "Lecturas cortas",
      headline: "Logline. Presentación. Sinopsis. Tratamiento.",
      body:
        "Los documentos que abren puertas son los más difíciles de escribir bien. Voxium Script evalúa cada documento corto según los estándares de oficio que aplican los profesionales en activo: claridad en una frase, riesgo que inclina a un comprador, estructura visible a través de la prosa. Logline, Documento de presentación, Sinopsis y Tratamiento comparten un mismo medidor de Lecturas cortas. Úsalo antes de enviar tu material. Úsalo antes de hacer tu pitch.",
      bestFor:
        "guionistas que preparan materiales que deben funcionar en menos de treinta segundos.",
      cta: "Evalúa un documento",
      testId: "card-short-form-es",
    },
  ],

  pricing: {
    eyebrow: "Precios",
    headline: "Accesible para guionistas serios, en cada nivel.",
    tiers: [
      {
        slug: "free",
        name: "Gratis",
        cadence: "de por vida",
        body:
          "Una lectura gratuita del Consejo, más una asignación de Fundamentos y de lecturas cortas. De por vida, sin necesidad de tarjeta de crédito.",
        testId: "tier-free-es",
      },
      {
        slug: "standard",
        name: "Standard",
        cadence: "al mes",
        body:
          "Una asignación mensual de lecturas del Consejo, todas las lecturas cortas, y una generosa cantidad mensual de lecturas de Fundamentos y reproducción de audio. Para guionistas en desarrollo activo.",
        testId: "tier-standard-es",
      },
      {
        slug: "pro",
        name: "Pro",
        cadence: "al mes",
        body:
          "Todo lo de Standard, más Simulación de reunión de pitch y límites mensuales más altos en todos los aspectos. Para guionistas que se preparan para la sala.",
        testId: "tier-pro-es",
      },
      {
        slug: "pro_plus",
        name: "Pro Plus",
        cadence: "al mes",
        body: "Todo lo de Pro, con los límites mensuales más altos: la mayor asignación de sesiones del Consejo, lecturas de Fundamentos, preguntas de seguimiento y reproducción de audio. Para guionistas que llevan varios proyectos en paralelo.",
        testId: "tier-pro-plus-es",
      },
    ],
    qa: {
      eyebrow: "Preguntas de seguimiento",
      intro:
        "Paquetes de preguntas de seguimiento que puedes hacerle a un lector del Consejo sobre un análisis que ya tienes. Cómpralos una vez, úsalos cuando quieras.",
      packs: [
        {
          slug: "qa_first_taste",
          name: "First Taste",
          body: "Tres preguntas de seguimiento. Disponible una sola vez por cuenta.",
          testId: "qa-pack-first-taste-es",
        },
        {
          slug: "qa_sip",
          name: "Sip",
          body: "Cinco preguntas de seguimiento.",
          testId: "qa-pack-sip-es",
        },
        {
          slug: "qa_deep_dive",
          name: "Deep Dive",
          body: "Veinte preguntas de seguimiento.",
          testId: "qa-pack-deep-dive-es",
        },
      ],
    },
    footnote: "Hay paquetes adicionales disponibles en medidores seleccionados. Cancela cuando quieras: tu trabajo, tu decisión.",
    currencyNote: "Todos los precios están en dólares estadounidenses (USD).",
    cta: "Empieza gratis",
  },

  pitchMeeting: {
    eyebrow: "Ensaya la sala antes de entrar",
    headline: "Simulación de reunión de pitch",
    body: "La sala no perdona nada. La simulación de reunión de pitch de Voxium Script te sienta frente a un Productor, un Productor Ejecutivo y un Director. Tú haces tu pitch: texto, presentación, tratamiento, o los tres. Ellos responden como lo harían en una reunión real. Te hacen las preguntas que te harán de verdad. Te dicen cómo perciben el proyecto, y en qué puntos pondrían resistencia.",
    frame: "Una emulación de un veredicto estándar de la industria: un indicador de dónde se posiciona tu proyecto frente a la sala, no una garantía de venta. Cada productor es distinto.",
    closing: "Haz tu pitch antes del pitch de verdad.",
    cta: "Prueba una simulación de pitch",
  },

  founderQuote: {
    text:
      "Construí esto para que mis propios guiones enfrentaran una crítica honesta antes de que lo hiciera la sala. Voxium Script te dice lo que ve, con claridad, con voz propia, sin suavizarlo. Así es el trabajo.",
    name: "Alain Raymond",
    role: "fundador",
  },

  footer: {
    legal: {
      privacy: "Privacidad",
      terms: "Términos",
      refunds: "Reembolsos",
      suffix: "(en inglés)",
    },
    legalNotice:
      "Los documentos legales se publican en inglés. En caso de discrepancia, prevalece la versión en inglés.",
    // DRAFT — pending native review. Added 2026-10-21 per Alain's
    // referral-capture brief: discloses the vox_ref cookie in Spanish.
    // Flagged for counsel re: Quebec Law 25 / consent, not decided here.
    referralNotice:
      "Si llegas a nuestro sitio a través de un enlace de un socio, colocamos una cookie llamada vox_ref para acreditar al socio que te refirió. Solo guarda el código de referencia — ningún dato personal — durante 90 días.",
    copyright: "© 2026 RayMar & Co Enterprises LLC",
  },

  // Fallback-state copy — same pattern as /fr's staleness banner: shown
  // only if the live /api/entitlements fetch fails.
  staleness: {
    prefix: "Los precios mostrados reflejan nuestros últimos valores conocidos",
    notice:
      "nuestra sincronización en vivo con la app no está disponible en este momento. Inicia sesión en",
    suffix: "para ver el catálogo y los límites actuales.",
  },

  // Header language picker — UI chrome string, centralized here per the
  // "single file" rule.
  pickerAriaLabel: "Elegir idioma",
};

export default es;
